import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTimePicker, Modal, Select, TrashIcon, toISODate, toLocalDate, useConfirm, useToast, type Option } from "@signet/ui";
import { useMe } from "@signet/web-kit";
import { OTHER_CURRENCIES, TOP_CURRENCIES } from "../shared/currencies.ts";
import { RATE_UNITS, type RateUnit } from "../shared/dates.ts";
import { createCategory, deleteCategory, exKeys, fetchCategories, renameCategory } from "./api.ts";

/* The pickers Expiries shares between its table and its Mark as Paid box. Every one is the kit's own menu or calendar, never the browser's. */

const NAMES = new Intl.DisplayNames(["en"], { type: "currency" });
const currencyName = (code: string) => {
  try {
    return NAMES.of(code) ?? code;
  } catch {
    return code;
  }
};

const CURRENCY_OPTIONS: Option[] = [
  ...TOP_CURRENCIES.map((code) => ({ value: code, label: code, hint: currencyName(code), group: "Most used" })),
  ...OTHER_CURRENCIES.map((code) => ({ value: code, label: code, hint: currencyName(code), group: "Others" })),
];

export function CurrencySelect({ value, onChange, disabled, compact }: { value: string; onChange: (code: string) => void; disabled?: boolean; compact?: boolean }) {
  return (
    <Select
      label="Currency"
      value={value}
      options={CURRENCY_OPTIONS}
      onChange={(code) => code && onChange(code)}
      disabled={disabled}
      compact={compact}
      searchable
      className="ex-currency"
      renderTrigger={(selected) => <span className="ex-currency-code">{selected?.label ?? value}</span>}
    />
  );
}

const UNIT_OPTIONS: Option<RateUnit>[] = RATE_UNITS.map((unit) => ({ value: unit, label: unit[0]!.toUpperCase() + unit.slice(1) }));

export function UnitSelect({ value, onChange, disabled }: { value: RateUnit | null; onChange: (unit: RateUnit) => void; disabled?: boolean }) {
  return <Select label="Expiry rate unit" value={value} options={UNIT_OPTIONS} onChange={(unit) => unit && onChange(unit)} disabled={disabled} compact searchable={false} placeholder="Days" className="ex-unit" />;
}

/** A calendar day (YYYY-MM-DD) in the kit's calendar. */
export function DayPicker({ value, onChange, label, disabled, clearable = true }: { value: string | null; onChange: (day: string | null) => void; label: string; disabled?: boolean; clearable?: boolean }) {
  if (disabled) return <span className="ex-static">{value ? new Date(`${value}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "Not set"}</span>;
  return (
    <DateTimePicker
      label={label}
      value={value}
      withTime={false}
      compact
      clearable={clearable}
      className="ex-day"
      renderValue={(iso) => (iso ? toLocalDate(iso)!.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "Not set")}
      onChange={(iso) => onChange(iso ? toISODate(new Date(iso)) : null)}
    />
  );
}

export function useCategories() {
  return useQuery({ queryKey: exKeys.categories, queryFn: fetchCategories, staleTime: 60_000 }).data?.categories ?? [];
}

/** The category menu: type a new name to make one, or open Edit categories to rename or remove them. */
export function CategorySelect({ value, onChange, disabled }: { value: string | null; onChange: (id: string | null) => void; disabled?: boolean }) {
  const { can } = useMe();
  const categories = useCategories();
  const client = useQueryClient();
  const toast = useToast();
  const [managing, setManaging] = useState(false);
  const make = async (name: string) => {
    try {
      const { category } = await createCategory(name);
      await client.invalidateQueries({ queryKey: exKeys.categories });
      onChange(category.id);
    } catch (error) {
      toast.error("Couldn't add the category", (error as Error).message);
    }
  };
  return (
    <>
      <Select
        label="Category"
        value={value}
        options={categories.map((c) => ({ value: c.id, label: c.name }))}
        onChange={onChange}
        disabled={disabled}
        compact
        clearable
        searchable
        placeholder="None"
        className="ex-category"
        onCreate={can("expiries.item:create") ? make : undefined}
        createLabel={(name) => `Add category “${name}”`}
        emptyLabel={categories.length ? "Nothing matches that." : "No categories yet. Type a name to add one."}
        footer={can("expiries.item:edit") || can("expiries.item:delete") ? (
          <button type="button" className="text-button tiny accent" onClick={() => setManaging(true)}>Edit categories</button>
        ) : null}
      />
      <CategoriesModal open={managing} onClose={() => setManaging(false)} />
    </>
  );
}

export function CategoriesModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { can } = useMe();
  const categories = useCategories();
  const client = useQueryClient();
  const toast = useToast();
  const { confirm, element } = useConfirm();
  const [adding, setAdding] = useState("");
  const refresh = () => Promise.all([client.invalidateQueries({ queryKey: exKeys.categories }), client.invalidateQueries({ queryKey: exKeys.items }), client.invalidateQueries({ queryKey: ["expiries", "upcoming"] })]);
  const run = useMutation({
    mutationFn: (job: () => Promise<unknown>) => job(),
    onSuccess: refresh,
    onError: (error) => toast.error("Couldn't save the category", (error as Error).message),
  });

  return (
    <Modal open={open} onClose={onClose} title="Categories" description="Rename one by editing its name. Removing one leaves its line items without a category.">
      <ul className="ex-cats">
        {categories.map((c) => (
          <li key={c.id}>
            <input
              defaultValue={c.name}
              aria-label={`Rename ${c.name}`}
              disabled={!can("expiries.item:edit")}
              onBlur={(event) => {
                const name = event.target.value.trim();
                if (name && name !== c.name) run.mutate(() => renameCategory(c.id, name));
                else event.target.value = c.name;
              }}
              onKeyDown={(event) => event.key === "Enter" && event.currentTarget.blur()}
            />
            <small>{c.items === 1 ? "1 item" : `${c.items} items`}</small>
            {can("expiries.item:delete") ? (
              <button
                type="button"
                className="icon-button ghost small danger"
                aria-label={`Remove ${c.name}`}
                onClick={async () => {
                  const ok = await confirm({ title: `Remove ${c.name}?`, body: c.items ? `Its ${c.items === 1 ? "line item stays" : `${c.items} line items stay`}, without a category.` : undefined, confirmLabel: "Remove", danger: true });
                  if (ok) run.mutate(() => deleteCategory(c.id));
                }}
              >
                <TrashIcon size={13} />
              </button>
            ) : null}
          </li>
        ))}
        {!categories.length ? <li className="ex-cats-empty">No categories yet.</li> : null}
      </ul>
      {can("expiries.item:create") ? (
        <form
          className="ex-cats-add"
          onSubmit={(event) => {
            event.preventDefault();
            const name = adding.trim();
            if (!name) return;
            run.mutate(() => createCategory(name), { onSuccess: () => setAdding("") });
          }}
        >
          <input value={adding} onChange={(event) => setAdding(event.target.value)} placeholder="New category" aria-label="New category" />
          <button type="submit" className="secondary-button compact" disabled={!adding.trim()}>Add</button>
        </form>
      ) : null}
      {element}
    </Modal>
  );
}
